import React from 'react';

import { Box, Container, Typography, Paper, List, ListItem, ListItemText, Divider } from '@mui/material';

const PrivacyPolicy: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom align="center">
            Política de Privacidad
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 4 }}>
            Última actualización: {new Date().toLocaleDateString('es-ES')}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" paragraph>
            Esta Política de Privacidad describe cómo nuestra aplicación recopila, usa y protege su información cuando
            utiliza nuestra funcionalidad Zeller Ads integrada con Meta (Facebook).
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            1. Información que recopilamos a través de Meta
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
            1.1 Datos de Perfil de Meta
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="ID de usuario de Meta"
                secondary="Identificador único proporcionado por Meta para asociar su cuenta"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Nombre de usuario"
                secondary="Su nombre tal como aparece en su perfil de Meta/Facebook"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Dirección de correo electrónico"
                secondary="Email asociado a su cuenta de Meta/Facebook (opcional)"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
            1.2 Tokens de Acceso y Autenticación
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Access Token de Meta"
                secondary="Token de acceso cifrado que nos permite acceder a su información publicitaria"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tipo de token" secondary="Tipo de autenticación utilizada (Bearer)" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Fecha de expiración del token"
                secondary="Fecha hasta la cual es válido el acceso a su información"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Estado de conexión" secondary="Si la conexión con Meta está activa o inactiva" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Fecha de última sincronización"
                secondary="Última vez que sincronizamos su información publicitaria"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
            1.3 Datos de Cuentas Publicitarias
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Información de cuentas publicitarias"
                secondary="ID, nombre, estado y moneda de sus cuentas publicitarias en Meta"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Datos de campañas publicitarias"
                secondary="Información sobre sus campañas: ID, nombre, estado, objetivos y presupuestos"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Conjuntos de anuncios (AdSets)"
                secondary="Información sobre los grupos de anuncios: configuración, targeting y presupuesto"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Anuncios individuales"
                secondary="Datos de anuncios específicos: creativos, texto, imágenes y rendimiento"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Métricas de rendimiento (Insights)"
                secondary="Estadísticas de rendimiento: impresiones, clics, conversiones, CTR, CPC, etc."
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            2. Cómo Utilizamos su Información
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Análisis de rendimiento publicitario"
                secondary="Para proporcionarle insights sobre el rendimiento de sus campañas"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Gestión de campañas"
                secondary="Para ayudarle a administrar y optimizar sus campañas publicitarias"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Reportes y análisis"
                secondary="Para generar reportes detallados sobre su actividad publicitaria"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Sincronización de datos"
                secondary="Para mantener actualizada la información de sus cuentas publicitarias"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            3. Permisos de Meta que Solicitamos
          </Typography>

          <List>
            <ListItem>
              <ListItemText primary="ads_read" secondary="Permiso para leer información de sus anuncios y campañas" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="ads_management"
                secondary="Permiso para gestionar sus anuncios y campañas (opcional)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="business_management"
                secondary="Permiso para acceder a la información de su negocio en Meta"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            4. Protección de Datos
          </Typography>

          <Typography variant="body1" paragraph>
            • Todos los tokens de acceso se almacenan de forma cifrada en nuestra base de datos
          </Typography>
          <Typography variant="body1" paragraph>
            • Verificamos regularmente la validez y expiración de los tokens
          </Typography>
          <Typography variant="body1" paragraph>
            • Puede revocar el acceso en cualquier momento desconectando su cuenta de Meta
          </Typography>
          <Typography variant="body1" paragraph>
            • Solo accedemos a los datos necesarios para proporcionar nuestros servicios
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            5. Sus Derechos
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Acceso a sus datos"
                secondary="Puede solicitar una copia de todos los datos que tenemos sobre usted"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Rectificación" secondary="Puede solicitar la corrección de datos inexactos" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Eliminación" secondary="Puede solicitar la eliminación de sus datos personales" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Desconexión"
                secondary="Puede desconectar su cuenta de Meta en cualquier momento desde la configuración"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            6. Retención de Datos
          </Typography>

          <Typography variant="body1" paragraph>
            Conservamos sus datos mientras su cuenta esté activa y conectada a Meta. Si desconecta su cuenta o elimina
            su perfil, eliminaremos todos los datos relacionados con Meta dentro de 30 días.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            7. Contacto
          </Typography>

          <Typography variant="body1" paragraph>
            Si tiene preguntas sobre esta Política de Privacidad o sobre cómo manejamos sus datos, puede contactarnos
            en: [Su dirección de correo de contacto]
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            8. Cumplimiento con Meta
          </Typography>

          <Typography variant="body1" paragraph>
            Esta aplicación cumple con las políticas de Meta para desarrolladores y las regulaciones de privacidad
            aplicables. Todos los datos se procesan de acuerdo con los estándares de Meta y las mejores prácticas de la
            industria.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            Al usar nuestra funcionalidad Zeller Ads, usted acepta esta Política de Privacidad y el procesamiento de sus
            datos como se describe aquí.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
